import { create } from "zustand";

import { PageName } from "analytics/events";

export type StockCheckSessionState = {
  checkId: string | null;
  origin: PageName;
  priority: number;
  shelfNumber: string | null;
  shelfLetter: string | null;
  // Product info
  sku: string | null;
  productName: string | null;
  productImageUrl: string | null;
  productCountryOfOriginName: string | null;
  // Quantities
  declaredQuantity: number;
  expectedQuantity: number;
  expiredQuantity: number;
  damagedQuantity: number;
  tooGoodToGoQuantity: number;
  remainingStock: number;
};

type InitializeCheckParams = {
  checkId: string;
  origin: PageName;
  shelfNumber: string | null;
  shelfLetter: string | null;
  priority?: number;
  sku?: string | null;
  productName?: string | null;
  productImageUrl?: string | null;
  productCountryOfOriginName?: string | null;
};

type DeclareStockDetailsParams = {
  expectedQuantity: number;
  remainingStock: number;
  damagedQuantity: number;
  expiredQuantity: number;
  tooGoodToGoQuantity: number;
};

type StockCheckSessionActions = {
  initializeCheck: (params: InitializeCheckParams) => void;
  declareQuantity: (declaredQuantity: number) => void;
  declareStockDetails: (details: DeclareStockDetailsParams) => void;
  reset: () => void;
};

export type StockCheckSessionStore = StockCheckSessionState & StockCheckSessionActions;

const initialState: StockCheckSessionState = {
  checkId: null,
  origin: PageName.INVENTORY_STOCK_CHECK_LIST_PAGE,
  priority: 0,
  shelfNumber: null,
  shelfLetter: null,
  sku: null,
  productName: null,
  productImageUrl: null,
  productCountryOfOriginName: null,
  declaredQuantity: 0,
  expectedQuantity: 0,
  expiredQuantity: 0,
  damagedQuantity: 0,
  tooGoodToGoQuantity: 0,
  remainingStock: 0,
};

export const useStockCheckSessionStore = create<StockCheckSessionStore>()((set) => ({
  ...initialState,
  initializeCheck: (params: InitializeCheckParams) =>
    set({
      checkId: params.checkId,
      origin: params.origin,
      shelfNumber: params.shelfNumber,
      shelfLetter: params.shelfLetter,
      priority: params.priority ?? 0,
      sku: params.sku ?? null,
      productName: params.productName ?? null,
      productImageUrl: params.productImageUrl ?? null,
      productCountryOfOriginName: params.productCountryOfOriginName ?? null,
      declaredQuantity: 0,
      expectedQuantity: 0,
      expiredQuantity: 0,
      damagedQuantity: 0,
      tooGoodToGoQuantity: 0,
      remainingStock: 0,
    }),
  declareQuantity: (declaredQuantity: number) => set({ declaredQuantity }),
  declareStockDetails: (details: DeclareStockDetailsParams) =>
    set({
      expectedQuantity: details.expectedQuantity,
      remainingStock: details.remainingStock,
      damagedQuantity: details.damagedQuantity,
      expiredQuantity: details.expiredQuantity,
      tooGoodToGoQuantity: details.tooGoodToGoQuantity,
    }),
  reset: () => set(initialState),
}));
